import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
	Box,
	Title,
	Text,
	Button,
	Textarea,
	Badge,
	Group,
	Grid,
	Modal,
	Divider,
	Card,
} from '@mantine/core'
import { useNavigate, useParams } from 'react-router-dom'
import {
	getCheckInById,
	updateCheckIn,
} from '../services/actions/checkInActions'
import PurchasedItemsModal from '../components/PurchasedItemsModal'
import PurchasedItemCard from '../components/PurchasedItemCard'

const CheckInDetailPage = () => {
	const { id, experienceSlug } = useParams()
	const navigate = useNavigate()

	const currentUser = useSelector((state) => state.user)
	const userPosition = useSelector((state) => state.experience.userPosition)
	const experienceDetails = useSelector(
		(state) => state.experience?.experienceDetails
	)
	const canAddItems =
		userPosition?.level === 'owner' || experienceDetails?.allowStaffAddItems

	const [checkIn, setCheckIn] = useState(null)
	const [purchasedItems, setPurchasedItems] = useState([])
	const [showAddItemModal, setShowAddItemModal] = useState(false)
	const [showNotes, setShowNotes] = useState(false)
	const [isCheckedIn, setIsCheckedIn] = useState(false)
	const [showWarningModal, setShowWarningModal] = useState(false)
	const [showCriticalModal, setShowCriticalModal] = useState(false)
	const [checkInNotes, setCheckInNotes] = useState(checkIn?.checkInNotes || [])
	const [noteMode, setNoteMode] = useState(null)
	const [noteDraftText, setNoteDraftText] = useState('')

	const fetchData = async () => {
		try {
			const checkInData = await getCheckInById(id)
			setCheckIn(checkInData)
			setPurchasedItems(checkInData.purchasedItems || [])
			setCheckInNotes(checkInData.checkInNotes || [])
			setIsCheckedIn(checkInData.checkInStatus || false)
			setShowNotes(checkInData.checkInNotes?.length > 0)
		} catch (err) {
			console.error('❌ Failed to fetch:', err)
			navigate(`/experiences/${experienceSlug}`)
		}
	}

	useEffect(() => {
		fetchData()
	}, [id, experienceSlug, navigate])

	const handlePatch = async (
		status,
		updatedNotes = checkInNotes,
		stopRedirect
	) => {
		const updated = {
			travelerName: checkIn.travelerName,
			travelerEmail: checkIn.travelerEmail,
			checkInStatus: status,
			checkInNotes: updatedNotes,
			purchasedItems: purchasedItems.map((item) => ({
				id: item.id,
				issued: item.issued,
				addedByUser: item.addedByUser,
				paymentMethod: item.paymentMethod,
				paymentAmount: item.paymentAmount,
			})),
		}

		try {
			await updateCheckIn(id, updated)
			await fetchData()

			if (status === true && stopRedirect !== true) {
				const refreshed = await getCheckInById(id)
				navigate(`/experiences/${experienceSlug}/success`, {
					state: { checkIn: refreshed },
				})
			}
		} catch (err) {
			console.error('❌ Failed to update check-in:', err)
		}
	}

	const toggleItem = (index) => {
		if (isCheckedIn) return

		const updated = [...purchasedItems]
		updated[index].issued = !updated[index].issued
		setPurchasedItems(updated)

		handlePatch(isCheckedIn)
	}

	const hasCriticalWarning = checkIn?.warnings?.some(
		(w) => w.level === 'Critical'
	)

	const handleAttemptFinalize = () => {
		const userLevel = userPosition?.level || 'staff'

		if (hasCriticalWarning && userLevel !== 'owner') {
			setShowCriticalModal(true)
			return
		}
		const anyUnissued = purchasedItems.some((item) => !item.issued)
		if (anyUnissued) {
			setShowWarningModal(true)
		} else {
			handleFinalize()
		}
	}

	const handleFinalize = () => handlePatch(true)
	const handleUndoCheckIn = () => handlePatch(false)
	const toggleNotes = () => {
		setShowNotes((prev) => !prev)
		setNoteMode(checkInNotes?.length === 0 && 'add')
	}
	const handleBack = () => navigate(`/experiences/${experienceSlug}`)

	const createNoteObject = (text) => {
		const now = new Date().toISOString()
		return {
			note: text.trim(),
			createdByUser: {
				id: currentUser?.authUser?.id,
				name: currentUser?.authUser?.name,
			},
			createdAtDateTime: now,
			lastModifiedDateTime: now,
		}
	}

	const handleSaveNote = async () => {
		const trimmed = noteDraftText.trim()
		if (!trimmed) return

		let updatedNotes = [...checkInNotes]

		if (noteMode === 'add') {
			updatedNotes.push(createNoteObject(trimmed))
		} else if (typeof noteMode === 'number') {
			updatedNotes[noteMode] = {
				...updatedNotes[noteMode],
				note: trimmed,
				lastModifiedDateTime: new Date().toISOString(),
			}
		}

		setCheckInNotes(updatedNotes)
		setNoteMode(null)
		setNoteDraftText('')
		await handlePatch(isCheckedIn, updatedNotes, true)
	}

	const handleCancelNote = () => {
		setNoteMode(null)
		setNoteDraftText('')
	}

	const handleDeleteNote = async (idx) => {
		const confirmed = window.confirm('Delete this note?')
		if (!confirmed) return

		const updatedNotes = checkInNotes.filter((_, i) => i !== idx)
		setCheckInNotes(updatedNotes)

		// Clear edit state if the deleted note was being edited
		if (noteMode === idx) {
			setNoteMode(null)
			setNoteDraftText('')
		}

		await handlePatch(isCheckedIn, updatedNotes, true)
	}

	const formatActualCheckInDateTime = (isoString) => {
		const date = new Date(isoString)
		const now = new Date()

		const isSameDay =
			date.getFullYear() === now.getFullYear() &&
			date.getMonth() === now.getMonth() &&
			date.getDate() === now.getDate()

		if (isSameDay) {
			// Only show time
			return date.toLocaleTimeString([], {
				hour: 'numeric',
				minute: '2-digit',
			})
		} else {
			// Show formatted date and time
			return (
				date.toLocaleDateString([], {
					month: '2-digit',
					day: '2-digit',
					year: '2-digit',
				}) +
				' at ' +
				date.toLocaleTimeString([], {
					hour: 'numeric',
					minute: '2-digit',
				})
			)
		}
	}

	if (!checkIn || !currentUser) return <Text>Loading...</Text>

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='xs'>
				{checkIn.travelerName}'s Check-In
			</Title>

			{Array.isArray(checkIn.warnings) && checkIn.warnings.length > 0 && (
				<Group spacing='xs' mt='sm' mb='md'>
					{checkIn.warnings.map((warning, idx) => (
						<Badge
							key={idx}
							color={
								warning.level === 'Critical'
									? 'red'
									: warning.level === 'Warning'
									? 'orange'
									: 'blue'
							}
							variant='filled'
							size='md'
						>
							{warning.title}
						</Badge>
					))}
				</Group>
			)}

			<Text>
				<strong>Email:</strong> {checkIn.travelerEmail}
			</Text>
			<Text>
				<strong>Phone:</strong> {checkIn.travelerPhone}
			</Text>
			<Text>
				<strong>Account:</strong> {checkIn.accountName}
			</Text>
			<Text>
				<strong>Station:</strong> {checkIn.stationName}
			</Text>
			<Text>
				<strong>Scheduled Check-in Date:</strong>{' '}
				{checkIn.scheduledCheckInDate
					? new Date(checkIn.scheduledCheckInDate).toLocaleDateString([], {
							month: 'long',
							day: 'numeric',
							year: 'numeric',
					  })
					: 'N/A'}
			</Text>
			<Text>
				<strong>Booking Number:</strong> {checkIn.bookingNumber}
			</Text>
			{!isCheckedIn && (
				<Text>
					<strong>Status:</strong> Not Checked-In
				</Text>
			)}
			{isCheckedIn && checkIn.actualCheckInDateTime && (
				<Badge color='green' variant='filled' size='lg' mt='xs'>
					Checked in at{' '}
					{formatActualCheckInDateTime(checkIn.actualCheckInDateTime)}
					{checkIn.checkedInBy?.name && <> by {checkIn.checkedInBy.name}</>}
				</Badge>
			)}

			<Divider my='md' />

			<Group align='center' spacing='xs' mt='md'>
				<Title order={4}>Purchased Items</Title>
				{canAddItems && (
					<Button
						size='xs'
						ml='xs'
						onClick={() => setShowAddItemModal(true)}
						disabled={hasCriticalWarning}
						variant='outline'
					>
						+ Add
					</Button>
				)}
			</Group>
			<Grid mt='sm'>
				{purchasedItems.map((item, idx) => (
					<Grid.Col key={idx} xs={12} sm={6} md={4}>
						<PurchasedItemCard
							name={item.name}
							imageUrl={item.imageUrl}
							issued={item.issued}
							disabled={isCheckedIn || hasCriticalWarning}
							onToggle={() => toggleItem(idx)}
						/>
					</Grid.Col>
				))}
			</Grid>

			<Group mt='lg'>
				{isCheckedIn ? (
					<Button color='red' onClick={handleUndoCheckIn}>
						Undo Check-In
					</Button>
				) : (
					<Button onClick={handleAttemptFinalize}>Finalize Check-In</Button>
				)}
				<Button variant='light' onClick={handleBack}>
					Back
				</Button>
			</Group>

			<Divider mt='xl' mb='xs' />

			<Button variant='subtle' color='blue' onClick={toggleNotes}>
				{showNotes
					? 'Hide Notes'
					: checkInNotes?.length > 0
					? 'Show Notes'
					: 'Add Notes'}
			</Button>

			{showNotes &&
				checkInNotes.map((note, idx) => {
					const isAuthor = note.createdByUser?.id === currentUser?.authUser?.id
					const isEditing = noteMode === idx

					return (
						<Card key={idx} shadow='sm' mt='sm' withBorder>
							{isEditing ? (
								<>
									<Textarea
										value={noteDraftText}
										onChange={(e) => setNoteDraftText(e.target.value)}
										minRows={3}
										mb='xs'
									/>
									<Group spacing='xs'>
										<Button
											size='xs'
											onClick={() => handleSaveNote()}
											disabled={!noteDraftText.trim()}
										>
											Save
										</Button>
										<Button
											size='xs'
											variant='light'
											color='gray'
											onClick={handleCancelNote}
										>
											Cancel
										</Button>
									</Group>
								</>
							) : (
								<Text>{note.note}</Text>
							)}
							<Group position='apart'>
								<Text size='sm' color='dimmed'>
									{note.createdByUser?.name || 'Unknown'} on{' '}
									{new Date(note.createdAtDateTime).toLocaleString()}
								</Text>
								{isAuthor && !isEditing && (
									<Group spacing='xs'>
										<Button
											size='xs'
											variant='light'
											onClick={() => {
												setNoteMode(idx)
												setNoteDraftText(note.note)
											}}
										>
											Edit
										</Button>
										<Button
											size='xs'
											variant='subtle'
											color='red'
											onClick={() => handleDeleteNote(idx)}
										>
											Delete
										</Button>
									</Group>
								)}
							</Group>
						</Card>
					)
				})}

			{showNotes && (
				<Box mt='md'>
					{noteMode !== 'add' ? (
						<Button
							onClick={() => {
								setNoteMode('add')
								setNoteDraftText('')
							}}
						>
							Add Note
						</Button>
					) : (
						<>
							<Textarea
								value={noteDraftText}
								onChange={(e) => setNoteDraftText(e.target.value)}
								placeholder='Add a new note here...'
								minRows={4}
							/>
							<Group mt='xs' spacing='xs'>
								<Button
									onClick={() => handleSaveNote()}
									disabled={!noteDraftText.trim()}
								>
									Save Note
								</Button>
								<Button variant='light' color='gray' onClick={handleCancelNote}>
									Cancel
								</Button>
							</Group>
						</>
					)}
				</Box>
			)}

			{/* Warning Modal */}
			<Modal
				opened={showWarningModal}
				onClose={() => {
					setShowWarningModal(false)
					setNoteMode(null)
					setNoteDraftText('')
				}}
				title='ARE YOU SURE?'
				centered
			>
				<Text mb='sm'>There are items you did not issue to the traveler.</Text>

				<Textarea
					value={noteDraftText}
					onChange={(e) => setNoteDraftText(e.target.value)}
					placeholder='Add an optional note...'
					minRows={4}
				/>

				<Group mt='md'>
					<Button
						variant='light'
						onClick={() => {
							setShowWarningModal(false)
							setNoteMode(null)
							setNoteDraftText('')
						}}
					>
						Go Back
					</Button>

					<Button
						onClick={async () => {
							setShowWarningModal(false)

							let updatedNotes = [...checkInNotes]

							if (noteDraftText.trim()) {
								const newNote = createNoteObject(noteDraftText)
								updatedNotes.push(newNote)
								setCheckInNotes(updatedNotes)
							}

							setNoteDraftText('')
							setNoteMode(null)
							await handlePatch(true, updatedNotes)
						}}
					>
						Yes, Check In Traveler
					</Button>
				</Group>
			</Modal>

			{/* Critical Modal */}
			<Modal
				opened={showCriticalModal}
				onClose={() => setShowCriticalModal(false)}
				title='HELP REQUIRED'
				centered
			>
				<Text>
					Please contact a supervisor.
					<br />
					This traveler cannot be checked-in without assistance.
				</Text>
				<Group mt='md'>
					<Button onClick={() => setShowCriticalModal(false)}>Go Back</Button>
				</Group>
			</Modal>

			{/* Add Items Modal*/}
			<PurchasedItemsModal
				show={showAddItemModal}
				onClose={() => setShowAddItemModal(false)}
				experienceSlug={experienceSlug}
				currentItems={purchasedItems}
				currentUserId={currentUser?.authUser?.id}
				onSave={async (newItems) => {
					try {
						const formattedItems = newItems.map((item) => ({
							id: item.id,
							issued: item.issued ?? false,
							paymentMethod: item.paymentMethod || '',
							paymentAmount: item.paymentAmount || '',
							addedByUser:
								item.paymentMethod && item.paymentMethod !== 'None'
									? currentUser?.id || ''
									: '',
						}))

						await updateCheckIn(id, {
							checkInStatus: isCheckedIn,
							checkInNotes,
							purchasedItems: formattedItems,
						})
						await fetchData()
						setShowAddItemModal(false)
					} catch (err) {
						console.error('❌ Failed to save updated purchased items:', err)
					}
				}}
			/>
		</Box>
	)
}

export default CheckInDetailPage
